/*
 * Valenor — Gallery controller.
 * - Toggles [data-active] on .vln-product-hero__media-slide when a thumb is
 *   clicked (desktop: CSS hides non-active; mobile: scroll-snap + programmatic
 *   scrollIntoView).
 * - Preloads the fullsize image of each thumb on idle so clicks are instant.
 * - Preloads again on pointerenter as a faster on-demand cache warm-up.
 */
(() => {
  if (typeof window === 'undefined' || window.__vlnGalleryInit) return;
  window.__vlnGalleryInit = true;

  const mqMobile = window.matchMedia('(max-width: 900px)');

  const preload = (url) => {
    if (!url) return;
    const img = new Image();
    img.decoding = 'async';
    img.loading = 'eager';
    img.src = url;
  };

  const preloadAll = () => {
    document
      .querySelectorAll('.vln-thumb[data-full-url]')
      .forEach((t) => preload(t.dataset.fullUrl));
  };

  const swap = (thumb) => {
    const hero = thumb.closest('.vln-product-hero__gallery, .vln-product-hero');
    if (!hero) return;
    const index = parseInt(thumb.dataset.index, 10);
    if (isNaN(index)) return;

    const slides = hero.querySelectorAll('.vln-product-hero__media-slide');
    slides.forEach((s, i) => {
      if (i === index) s.setAttribute('data-active', '');
      else s.removeAttribute('data-active');
    });

    if (mqMobile.matches && slides[index]) {
      slides[index].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }

    hero.querySelectorAll('.vln-thumb').forEach((t) =>
      t.classList.remove('vln-thumb--active')
    );
    thumb.classList.add('vln-thumb--active');
  };

  // Warm fullsize images on hover / touchstart.
  document.addEventListener(
    'pointerenter',
    (e) => {
      const thumb = e.target && e.target.closest ? e.target.closest('.vln-thumb') : null;
      if (thumb && thumb.dataset.fullUrl) preload(thumb.dataset.fullUrl);
    },
    true
  );

  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('.vln-thumb');
    if (!thumb) return;
    e.preventDefault();
    swap(thumb);
  });

  // Desktop prev/next arrows (inert on mobile via CSS).
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-vln-gallery-arrow]');
    if (!btn) return;
    e.preventDefault();
    const direction = btn.dataset.vlnGalleryArrow;
    const hero = btn.closest('.vln-product-hero');
    if (!hero) return;
    const slides = Array.from(
      hero.querySelectorAll('.vln-product-hero__media-slide[data-media-id]')
    );
    if (slides.length < 2) return;
    const current = hero.querySelector(
      '.vln-product-hero__media-slide[data-active]'
    );
    let idx = current ? slides.indexOf(current) : 0;
    idx = direction === 'next'
      ? (idx + 1) % slides.length
      : (idx - 1 + slides.length) % slides.length;
    const next = slides[idx];
    const nextThumb = hero.querySelector(
      `.vln-thumb[data-media-id="${next.dataset.mediaId}"]`
    );
    if (nextThumb) swap(nextThumb);
    else {
      slides.forEach((s) => s.removeAttribute('data-active'));
      next.setAttribute('data-active', '');
    }
  });

  // Preload all after initial paint; respect prefers-reduced-data where exposed.
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 600));
  const reducedData =
    window.matchMedia && window.matchMedia('(prefers-reduced-data: reduce)').matches;
  if (!reducedData) idle(preloadAll);

  // On mount, align mobile scroll to the default active slide + set up an
  // IntersectionObserver so the thumb active border follows the scroll.
  const setupMobileScrollSync = () => {
    if (!mqMobile.matches) return;
    const active = document.querySelector(
      '.vln-product-hero__media-slide[data-active]'
    );
    if (active) active.scrollIntoView({ block: 'nearest', inline: 'center' });

    if (!('IntersectionObserver' in window)) return;
    const track = document.querySelector('.vln-product-hero__media-track');
    const slides = document.querySelectorAll(
      '.vln-product-hero__media-slide[data-media-id]'
    );
    if (!track || slides.length === 0) return;

    const markActive = (mediaId) => {
      if (!mediaId) return;
      document
        .querySelectorAll('.vln-thumb')
        .forEach((t) => t.classList.remove('vln-thumb--active'));
      const thumb = document.querySelector(
        `.vln-thumb[data-media-id="${mediaId}"]`
      );
      if (!thumb) return;
      thumb.classList.add('vln-thumb--active');

      // Keep the active thumb visible inside its scroll container, but only
      // when that container is actually scrollable (mobile case).
      const thumbRow = thumb.closest('.vln-product-hero__thumbs');
      if (thumbRow && thumbRow.scrollWidth > thumbRow.clientWidth + 1) {
        const rowRect = thumbRow.getBoundingClientRect();
        const thumbRect = thumb.getBoundingClientRect();
        if (thumbRect.left < rowRect.left || thumbRect.right > rowRect.right) {
          const offsetLeft = thumb.offsetLeft;
          const target =
            offsetLeft - thumbRow.clientWidth / 2 + thumb.clientWidth / 2;
          thumbRow.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
        }
      }
    };

    const io = new IntersectionObserver(
      (entries) => {
        // Pick the entry most visible this batch.
        let best = null;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (!best || entry.intersectionRatio > best.intersectionRatio) {
            best = entry;
          }
        }
        if (best && best.target.dataset.mediaId) {
          markActive(best.target.dataset.mediaId);
        }
      },
      { root: track, threshold: [0.55, 0.75, 0.9] }
    );
    slides.forEach((s) => io.observe(s));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupMobileScrollSync, { once: true });
  } else {
    setupMobileScrollSync();
  }
})();
